import React,{createContext,useContext,useEffect,useMemo,useState} from 'react';
import {apiRequest,authStorage} from '../api/client';

const AuthContext=createContext(null);

function getProfileValidationError(profile) {
  if (!profile || typeof profile!=='object') {
    return `The /auth/me response was not an object: ${JSON.stringify(profile)}`;
  }

  const missing=[];

  if (!profile._id && !profile.id) {
    missing.push('_id or id');
  }

  if (!['admin','student'].includes(profile.role)) {
    missing.push('role must be admin or student');
  }

  if (missing.length) {
    return `/auth/me returned an invalid profile; ${missing.join(', ')}. Response: ${JSON.stringify(profile)}`;
  }

  return '';
}

function validateProfile(profile) {
  const error=getProfileValidationError(profile);

  if (error) {
    throw new Error(error);
  }

  return profile;
}

export function AuthProvider({children}) {
  const [user,setUser]=useState(null);
  const [loading,setLoading]=useState(()=> Boolean(authStorage.get()));

  useEffect(()=> {
    let active=true;

    const restoreSession=async ()=> {
      const token=authStorage.get();

      if (!token) {
        if (active) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const profile=await apiRequest('/auth/me',{token});
        validateProfile(profile);

        if (active) {
          setUser(profile);
        }
      } catch (error) {
        console.warn('Stored authentication session is invalid:',error);
        authStorage.clear();

        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    restoreSession();

    return ()=> {
      active=false;
    };
  },[]);

  const login=async (email,password)=> {
    setLoading(true);
    setUser(null);

    try {
      const loginResponse=await apiRequest('/auth/login',{
        method: 'POST',
        auth: false,
        body: JSON.stringify({email,password})
      });

      const token=loginResponse?.token;

      if (typeof token!=='string' || !token.trim()) {
        throw new Error(
          `Login response did not contain response.token. Response: ${JSON.stringify(loginResponse)}`
        );
      }

      authStorage.set(token);

      const profile=await apiRequest('/auth/me',{token});
      validateProfile(profile);
      setUser(profile);

      return profile;
    } catch (error) {
      console.error('Authentication failed:',error);
      authStorage.clear();
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout=async ()=> {
    try {
      const token=authStorage.get();

      if (token) {
        await apiRequest('/auth/logout',{
          method: 'POST',
          token
        });
      }
    } catch (error) {
      console.warn('Logout request failed:',error);
    } finally {
      authStorage.clear();
      setUser(null);
      setLoading(false);
    }
  };

  const value=useMemo(
    ()=> ({user,loading,login,logout}),
    [user,loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context=useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}